// In Editor.js

import React, { useEffect } from 'react';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import Paragraph from '@editorjs/paragraph';
// import ParagraphWithReferences from '../components/ParagraphWithReferences'; // DISABLED: No more citation parsing
import List from '@editorjs/list';
import Table from '@editorjs/table';
import Delimiter from '@editorjs/delimiter';
import Image from '@editorjs/image';
import { uploadImageToDirectus } from '../../features/articles';

const Editor = ({ initialData, onChange, editorRef, title }) => {
  useEffect(() => {
    if (!editorRef.current) {
      let blocks = initialData?.blocks || [];

      // Check if the first block is already an H1 header
      if (!blocks.length || blocks[0].type !== 'header' || blocks[0].data.level !== 1) {
        // If not, prepend the title as an H1 header
        blocks = [
          {
            type: 'header',
            data: {
              text: title,
              level: 1
            }
          },
          ...blocks
        ];
      } else {
        // If it is, update the text of the first block
        blocks[0].data.text = title;
      }

      // Remove any [object Object] blocks and invalid blocks
      blocks = blocks.filter(block => {
        // Filter out invalid paragraph blocks
        if (block.type === 'paragraph' && block.data.text === '[object Object]') {
          return false;
        }
        
        // Fix invalid image blocks
        if (block.type === 'image') {
          const imageUrl = block?.data?.file?.url || block?.data?.url;
          if (!imageUrl) {
            return false;
          }
        }

        if (block.type === 'image' && (!block.data || (!block.data.file && !block.data.url))) {
          return false;
        }
        
        return true;
      });

      // Pre-process image blocks to ensure they have valid URLs
      blocks = blocks.map(block => {
        if (block.type === 'image' && block.data) {
          const imageUrl = block?.data?.file?.url || block?.data?.url;
          if (!imageUrl) {
            return block;
          }

          const normalizedUrl = imageUrl.replace('http://', 'https://');

          // Fix image URLs to ensure they're allowed by CSP
          if (normalizedUrl.includes('pexels.com')) {
            return {
              ...block,
              data: {
                ...block.data,
                file: {
                  ...(block.data.file || {}),
                  // Make sure the URL uses https
                  url: normalizedUrl
                },
                url: undefined
              }
            };
          }
          
          // Fix Directus image URLs
          if (normalizedUrl.includes('/assets/')) {
            return {
              ...block,
              data: {
                ...block.data,
                file: {
                  ...(block.data.file || {}),
                  url: normalizedUrl
                },
                url: undefined
              }
            };
          }

          // Generic fallback for any other valid image URL
          return {
            ...block,
            data: {
              ...block.data,
              file: {
                ...(block.data.file || {}),
                url: normalizedUrl
              },
              url: undefined
            }
          };
        }
        return block;
      });

      editorRef.current = new EditorJS({
        holder: 'editorjs',
        tools: {
          header: {
            class: Header,
            inlineToolbar: ['link'],
            config: {
              placeholder: 'Enter a header',
              levels: [1, 2, 3, 4, 5, 6],
              defaultLevel: 2,
            },
          },
          delimiter: Delimiter,
          paragraph: {
            class: Paragraph,
            inlineToolbar: true
          },
          list: {
            class: List,
            inlineToolbar: true,
          },
          table: {
            class: Table,
            inlineToolbar: true,
            config: {
              rows: 2,
              cols: 3,
            },
          },

          image: {
            class: Image,
            config: {
              // Use data URLs when possible to avoid CSP issues
              useDataUrls: true,
              captionPlaceholder: 'Image caption...',
              uploader: {
                uploadByFile: async (file) => {
                  try {
                    const result = await uploadImageToDirectus(file);
                    if (result.success) {
                      console.log('Image upload successful:', result);
                      return {
                        success: 1,
                        file: {
                          url: result.file.url,
                          id: result.file.id
                        }
                      };
                    } else {
                      console.error('Upload failed:', result.error);
                      return {
                        success: 0,
                        message: result.error || 'Upload failed'
                      };
                    }
                  } catch (error) {
                    console.error('Error uploading image:', error);
                    return {
                      success: 0,
                      message: error.message || 'An unknown error occurred'
                    };
                  }
                },
                uploadByUrl: async (url) => {
                  try {
                    // For external images, ensure they're using https
                    if (url.includes('pexels.com')) {
                      url = url.replace('http://', 'https://');
                    }
                    
                    // For URL uploads, you might want to fetch and upload the image to your server
                    // to avoid cross-origin issues
                    return {
                      success: 1,
                      file: {
                        url: url,
                        // You might want to generate a unique ID
                        id: `url-${Date.now()}`
                      }
                    };
                  } catch (error) {
                    console.error('Error uploading image by URL:', error);
                    return {
                      success: 0,
                      message: error.message || 'Upload by URL failed'
                    };
                  }
                }
              }
            }
          }
        },
        data: { blocks },
        onChange: onChange,
        // Use the 'rendered' hook instead of the deprecated 'appendCallback'
        onReady: () => {
          console.log('Editor.js is ready');
          
          // Override the inline padding-bottom style
          const overridePadding = () => {
            const redactor = document.querySelector('.codex-editor__redactor');
            if (redactor) {
              redactor.style.setProperty('padding-bottom', '32px', 'important');
              console.log('EditorJS padding-bottom overridden');
            }
          };
          
          // Initial override
          setTimeout(overridePadding, 100);
          
          // Watch for changes and re-override
          const observer = new MutationObserver(() => {
            overridePadding();
          });
          
          setTimeout(() => {
            const redactor = document.querySelector('.codex-editor__redactor');
            if (redactor) {
              observer.observe(redactor, { 
                attributes: true, 
                attributeFilter: ['style'] 
              });
            }
          }, 200);
        },
        // Set up proper error handling
        logLevel: 'ERROR'
      });
    }

    return () => {
      if (editorRef.current && editorRef.current.destroy) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, [initialData, onChange, editorRef, title]);

  return <div id="editorjs" className="bg-white px-8 rounded-lg border border-neutral-200 pt-4"></div>;
};

export default Editor;